import React, { useState } from 'react';
import { X, Plus, Minus, Package, Loader2, AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';

const ADJUSTMENT_REASONS = [
    { value: 'purchase', label: 'Purchase/Restock', type: 'add' },
    { value: 'return_from_customer', label: 'Customer Return', type: 'add' },
    { value: 'found', label: 'Found/Inventory Count', type: 'add' },
    { value: 'sale', label: 'Sale', type: 'subtract' },
    { value: 'damage', label: 'Damaged/Expired', type: 'subtract' },
    { value: 'lost', label: 'Lost/Stolen', type: 'subtract' },
    { value: 'return_to_supplier', label: 'Return to Supplier', type: 'subtract' },
    { value: 'adjustment', label: 'Inventory Adjustment', type: 'both' },
    { value: 'other', label: 'Other', type: 'both' },
];

const StockAdjustModal = ({ product, type = 'add', onSave, onClose, isSaving }) => {
    const [adjustType, setAdjustType] = useState(type);
    const [quantity, setQuantity] = useState('');
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    const [error, setError] = useState(null);

    const currentStock = product?.quantity || 0;

    const getNewStock = () => {
        const qty = parseInt(quantity) || 0;
        if (adjustType === 'add') return currentStock + qty;
        if (adjustType === 'subtract') return Math.max(0, currentStock - qty);
        return currentStock;
    };

    const newStock = getNewStock();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        const qty = parseInt(quantity);
        if (!qty || qty <= 0) {
            setError('Please enter a valid quantity');
            return;
        }

        if (!reason) {
            setError('Please select a reason');
            return;
        }

        if (adjustType === 'subtract' && qty > currentStock) {
            setError('Cannot remove more than current stock');
            return;
        }

        try {
            await onSave({
                type: adjustType,
                quantity: qty,
                reason,
                notes,
                new_quantity: newStock,
            });
        } catch (err) {
            setError(err.message || 'Failed to adjust stock');
        }
    };

    const filteredReasons = ADJUSTMENT_REASONS.filter(
        r => r.type === 'both' || r.type === adjustType
    );

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn"
            onClick={onClose}
        >
            <div
                className="w-full max-w-md bg-[#0d1424] border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-slideUp"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                            adjustType === 'add' ? 'bg-emerald-500/10' : 'bg-red-500/10'
                        }`}>
                            {adjustType === 'add' ? (
                                <ArrowUp className="w-5 h-5 text-emerald-500" />
                            ) : (
                                <ArrowDown className="w-5 h-5 text-red-500" />
                            )}
                        </div>
                        <h2 className="text-xl font-bold text-white">
                            {adjustType === 'add' ? 'Add Stock' : 'Remove Stock'}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Product Info */}
                <div className="p-4 bg-white/[0.02] border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-white/5 rounded-lg flex items-center justify-center overflow-hidden">
                            {product?.images && product.images.length > 0 ? (
                                <img
                                    src={product.images[0]}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <Package size={20} className="text-white/30" />
                            )}
                        </div>
                        <div>
                            <h3 className="font-semibold text-white">{product?.name}</h3>
                            <p className="text-sm text-white/50">
                                Current stock: <span className="font-medium text-white">{currentStock} {product?.unit || 'pcs'}</span>
                            </p>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    {/* Type Toggle */}
                    <div className="flex bg-white/5 p-1 rounded-lg">
                        <button
                            type="button"
                            onClick={() => setAdjustType('add')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition ${
                                adjustType === 'add'
                                    ? 'bg-emerald-500 text-white'
                                    : 'text-white/50 hover:text-white'
                            }`}
                        >
                            <Plus size={16} />
                            Add Stock
                        </button>
                        <button
                            type="button"
                            onClick={() => setAdjustType('subtract')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition ${
                                adjustType === 'subtract'
                                    ? 'bg-red-500 text-white'
                                    : 'text-white/50 hover:text-white'
                            }`}
                        >
                            <Minus size={16} />
                            Remove Stock
                        </button>
                    </div>

                    {/* Quantity */}
                    <div>
                        <label className="block text-sm font-medium text-white/70 mb-2">
                            Quantity *
                        </label>
                        <input
                            type="number"
                            min="1"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-lg font-semibold text-center placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition"
                            placeholder="0"
                            autoFocus
                        />
                    </div>

                    {/* New Stock Preview */}
                    {quantity && (
                        <div className={`p-4 rounded-lg border ${
                            adjustType === 'add' ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'
                        }`}>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-white/50">New stock level:</span>
                                <span className={`font-bold text-lg ${
                                    adjustType === 'add' ? 'text-emerald-500' : 'text-red-500'
                                }`}>
                                    {newStock} {product?.unit || 'pcs'}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 mt-2 text-xs text-white/40">
                                <span>{currentStock}</span>
                                <span>{adjustType === 'add' ? '+' : '-'}</span>
                                <span>{quantity}</span>
                                <span>=</span>
                                <span className="font-medium">{newStock}</span>
                            </div>
                        </div>
                    )}

                    {/* Reason */}
                    <div>
                        <label className="block text-sm font-medium text-white/70 mb-2">
                            Reason *
                        </label>
                        <select
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition appearance-none"
                        >
                            <option value="">Select a reason...</option>
                            {filteredReasons.map(r => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-sm font-medium text-white/70 mb-2">
                            Notes (optional)
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition resize-none"
                            placeholder="Add any notes about this adjustment..."
                        />
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
                            <AlertTriangle size={16} />
                            {error}
                        </div>
                    )}
                </form>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10 bg-[#0a0f1c]">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-white/60 hover:text-white rounded-lg hover:bg-white/5 transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSaving || !quantity || !reason}
                        className={`px-6 py-2 text-white rounded-lg font-medium flex items-center gap-2 transition disabled:opacity-50 ${
                            adjustType === 'add'
                                ? 'bg-emerald-500 hover:bg-emerald-600'
                                : 'bg-red-500 hover:bg-red-600'
                        }`}
                    >
                        {isSaving ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                {adjustType === 'add' ? <Plus size={18} /> : <Minus size={18} />}
                                {adjustType === 'add' ? 'Add Stock' : 'Remove Stock'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StockAdjustModal;
